import { Alert } from 'react-native';
import {
  hasBiometricOfferBeenDecided,
  hasStoredCredentials,
  isDeviceBiometryReady,
  markBiometricOfferDecided,
  setCredentials,
} from './BiometricKeychain';

/**
 * Biometric credential save after login:
 * - Ask the enable alert **once** per device.
 * - If the user already opted in (credentials on device), silently refresh them every login.
 * - If they declined (or dismissed), do not ask again.
 */
export function offerSaveBiometricLogin(
  t: (key: string) => string,
  phone: string,
  password: string,
): Promise<void> {
  return new Promise(resolve => {
    void (async () => {
      try {
        const [hardware_ok, already_saved, already_decided] = await Promise.all([
          isDeviceBiometryReady(),
          hasStoredCredentials(),
          hasBiometricOfferBeenDecided(),
        ]);

        if (!hardware_ok) {
          resolve();
          return;
        }

        // Already opted in — keep Face ID / fingerprint credentials fresh on every login.
        if (already_saved) {
          await setCredentials(phone, password).catch(() => {});
          resolve();
          return;
        }

        // Declined (or answered) with no saved creds — do not re-prompt.
        if (already_decided) {
          resolve();
          return;
        }

        Alert.alert(
          t('auth.enableBiometrics'),
          t('auth.enableBiometricsMessage'),
          [
            {
              text: t('common.no'),
              style: 'cancel',
              onPress: () => {
                void markBiometricOfferDecided()
                  .catch(() => {})
                  .finally(() => resolve());
              },
            },
            {
              text: t('common.yes'),
              onPress: () => {
                void setCredentials(phone, password)
                  .catch(() => {})
                  .finally(() => resolve());
              },
            },
          ],
          {
            cancelable: true,
            onDismiss: () => {
              void markBiometricOfferDecided()
                .catch(() => {})
                .finally(() => resolve());
            },
          },
        );
      } catch {
        resolve();
      }
    })();
  });
}
